import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { Search, Eye, X, Mail, Phone, Headphones, Download as DownloadIcon, ChevronDown, ChevronUp, HelpCircle, CheckCircle2, XCircle, Activity, PhoneOff, ThumbsDown, FileText, PhoneCall } from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import { useJob } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const RecallStatWidget = ({ title, value, icon: Icon, color, active, onClick }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)',
      bgcolor: active ? `${color}08` : 'background.paper',
      cursor: onClick ? 'pointer' : 'default',
    }}
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const getDossierStatusLabel = (status) => {
  if (!status) return '';
  const map = {
    documents_sent: 'Documenti inviati',
    failed: 'Fallito',
    'in negotiation': 'In trattativa',
    processing: 'In lavorazione',
    submitted_quote: 'Preventivo inviato',
    terminated: 'Terminato',
  };
  return map[status] || status;
};

const getRecallOutcomeLabel = (esito) => {
  if (!esito) return '';
  const map = {
    non_risposto: 'Non risposto',
    non_buono: 'Non buono',
    preventivo_scritto: 'Preventivo scritto',
    richiamare: 'Richiamare',
    cliente: 'Cliente'
  };
  return map[esito] || esito;
};

const getWorkflowIconConfig = (esito, stato) => {
  switch (esito) {
    case 'non_risposto':
      return { Icon: PhoneOff, color: '#0ea5e9', bg: '#e0f2fe' };
    case 'non_buono':
      return { Icon: ThumbsDown, color: '#f97316', bg: '#ffedd5' };
    case 'preventivo_scritto':
      return { Icon: FileText, color: '#6366f1', bg: '#e0e7ff' };
    case 'richiamare':
      return { Icon: PhoneCall, color: '#0ea5e9', bg: '#e0f2fe' };
    case 'cliente':
      return { Icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' };
    default: {
      if (stato === 'gestito') {
        return { Icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' };
      }
      if (stato === 'non_gestire') {
        return { Icon: XCircle, color: '#dc2626', bg: '#fee2e2' };
      }
      return { Icon: HelpCircle, color: '#0ea5e9', bg: '#e0f2fe' };
    }
  }
};

export default function DossierRecall() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterContractType, setFilterContractType] = useState('');
  const [filterRespComm, setFilterRespComm] = useState('');
  const [filterRespCont, setFilterRespCont] = useState('');
  const [filterRespUpsell, setFilterRespUpsell] = useState('');
  const [filterStatoPreventivo, setFilterStatoPreventivo] = useState('');
  const [filterRecallStatus, setFilterRecallStatus] = useState('all');
  const [filterRecallOutcome, setFilterRecallOutcome] = useState('');
  const [filterNegotiationCategory, setFilterNegotiationCategory] = useState([]);
  const [filterNegotiationIssuer, setFilterNegotiationIssuer] = useState('');
  const [filterNegotiationStatus, setFilterNegotiationStatus] = useState([]);
  const [filterDataConfFrom, setFilterDataConfFrom] = useState('');
  const [filterDataConfTo, setFilterDataConfTo] = useState('');
  const [filterDataInvioDocFrom, setFilterDataInvioDocFrom] = useState('');
  const [filterDataInvioDocTo, setFilterDataInvioDocTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [userNames, setUserNames] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([]);
  const [statiPreventivoOptions, setStatiPreventivoOptions] = useState([]);
  const [negotiationCategoryOptions, setNegotiationCategoryOptions] = useState([]);
  const [negotiationIssuerOptions, setNegotiationIssuerOptions] = useState([]);
  const [negotiationStatusOptions, setNegotiationStatusOptions] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({
    id: false,
    responsabile: true,
    acconto: true,
    dataInvioDocumenti: true,
    dataConferimento: true,
  });
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [cloudtalkDialogOpen, setCloudtalkDialogOpen] = useState(false);
  const [cloudtalkTag, setCloudtalkTag] = useState('');
  const [cloudtalkStripPhonePrefix, setCloudtalkStripPhonePrefix] = useState(false);
  const [cloudtalkSeparator, setCloudtalkSeparator] = useState(',');
  const [cloudtalkExporting, setCloudtalkExporting] = useState(false);
  const [standardExporting, setStandardExporting] = useState(false);
  const [standardExportDialogOpen, setStandardExportDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [rowNegotiations, setRowNegotiations] = useState({});
  const [rowNegotiationsLoading, setRowNegotiationsLoading] = useState({});
  const [recallDialogOpen, setRecallDialogOpen] = useState(false);
  const [recallRow, setRecallRow] = useState(null);
  const [recallOutcome, setRecallOutcome] = useState('');
  const [recallNote, setRecallNote] = useState('');
  const [recallSaving, setRecallSaving] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowRows, setWorkflowRows] = useState([]);
  const [workflowDossier, setWorkflowDossier] = useState(null);
  const { addJob } = useJob();
  const { user } = useAuth();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const hasDateFilters =
    !!filterDataConfFrom || !!filterDataConfTo || !!filterDataInvioDocFrom || !!filterDataInvioDocTo;

  const handleClearDateFilters = () => {
    setFilterDataConfFrom('');
    setFilterDataConfTo('');
    setFilterDataInvioDocFrom('');
    setFilterDataInvioDocTo('');
    setPage(0);
  };

  const buildFilterParams = () => {
    const params = {};
    if (filterSearch) params.search = filterSearch;
    if (filterStatus) params.status = filterStatus;
    if (filterContractType) params.contractType = filterContractType;
    if (filterRespComm) params.respComm = filterRespComm;
    if (filterRespCont) params.respCont = filterRespCont;
    if (filterRespUpsell) params.respUpsell = filterRespUpsell;
    if (filterStatoPreventivo) params.statoPreventivo = filterStatoPreventivo;
    if (Array.isArray(filterNegotiationCategory) && filterNegotiationCategory.length > 0) {
      params.negotiationCategory = filterNegotiationCategory;
    }
    if (filterNegotiationIssuer) params.negotiationIssuer = filterNegotiationIssuer;
    if (Array.isArray(filterNegotiationStatus) && filterNegotiationStatus.length > 0) {
      params.negotiationStatus = filterNegotiationStatus;
    }
    if (filterDataConfFrom) params.dataConferimentoFrom = filterDataConfFrom;
    if (filterDataConfTo) params.dataConferimentoTo = filterDataConfTo;
    if (filterDataInvioDocFrom) params.dataInvioDocumentiFrom = filterDataInvioDocFrom;
    if (filterDataInvioDocTo) params.dataInvioDocumentiTo = filterDataInvioDocTo;
    if (filterRecallStatus && filterRecallStatus !== 'all') params.recallStatus = filterRecallStatus;
    if (filterRecallOutcome) params.recallOutcome = filterRecallOutcome;
    return params;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order,
        ...buildFilterParams(),
      };

      const res = await api.get('/dossier', { params });
      setList(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching dossier recall list:', error);
      toast.error('Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  };

  const optionalColumnsActive =
    columnVisibility.id ||
    columnVisibility.responsabile ||
    columnVisibility.acconto ||
    columnVisibility.dataInvioDocumenti ||
    columnVisibility.dataConferimento;

  const baseColumnsCount = 6;
  const extraColumnsCount =
    (columnVisibility.id ? 1 : 0) +
    (columnVisibility.responsabile ? 1 : 0) +
    (columnVisibility.acconto ? 1 : 0) +
    (columnVisibility.dataInvioDocumenti ? 1 : 0) +
    (columnVisibility.dataConferimento ? 1 : 0);
  const totalColumns = baseColumnsCount + extraColumnsCount;

  const esitoStats = useMemo(() => {
    const stats = {
      non_risposto: 0,
      non_buono: 0,
      preventivo_scritto: 0,
      richiamare: 0,
      cliente: 0,
    };
    list.forEach((item) => {
      const esito = item.recall_last_outcome;
      if (esito && Object.prototype.hasOwnProperty.call(stats, esito)) {
        stats[esito] += 1;
      }
    });
    return stats;
  }, [list]);

  const handleCloudtalkExport = async () => {
    try {
      setCloudtalkExporting(true);
      setCloudtalkDialogOpen(false);
      const payload = {
        ...buildFilterParams(),
        tag: cloudtalkTag || '',
        stripPhonePrefix39: cloudtalkStripPhonePrefix,
        separator: cloudtalkSeparator
      };
      const res = await api.post('/dossier/recall/export', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'cloudtalk_recall_csv');
        toast.info('Export CSV per Cloudtalk avviato. Riceverai una notifica al completamento.', {
          position: 'top-right',
          autoClose: 4000
        });
      } else {
        toast.error('Impossibile avviare export CSV per Cloudtalk');
      }
    } catch (error) {
      console.error('Error starting Cloudtalk CSV export:', error);
      toast.error('Errore nell\'avvio dell\'export CSV per Cloudtalk');
    } finally {
      setCloudtalkExporting(false);
      setCloudtalkTag('');
      setCloudtalkStripPhonePrefix(false);
      setCloudtalkSeparator(',');
    }
  };

  const handleStandardExport = async () => {
    try {
      setStandardExporting(true);
      const payload = {
        ...buildFilterParams(),
        format: 'standard'
      };
      const res = await api.post('/dossier/recall/export', payload);
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'dossier_recall_csv');
        toast.info('Export CSV recall avviato. Riceverai una notifica al completamento.', {
          position: 'top-right',
          autoClose: 4000
        });
      } else {
        toast.error('Impossibile avviare export CSV recall');
      }
    } catch (error) {
      console.error('Error starting recall CSV export:', error);
      toast.error('Errore nell\'avvio dell\'export CSV recall');
    } finally {
      setStandardExporting(false);
    }
  };

  const formatDateLabel = (value) => {
    if (!value || typeof value !== 'string') return '';
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return value;
    const [, y, mo, d] = m;
    return `${d}/${mo}/${y}`;
  };

  const getRecallStatusLabel = (value) => {
    if (!value || value === 'all') return 'Tutti';
    if (value === 'da_gestire') return 'Da gestire';
    if (value === 'gestito') return 'Gestito';
    if (value === 'non_gestire') return 'Non gestire';
    return value;
  };

  const getStandardExportFilterChips = () => {
    const params = buildFilterParams();
    const chips = [];

    const asList = (value) => {
      const list = Array.isArray(value)
        ? value
        : String(value || '')
            .split(',')
            .map((v) => v.trim());
      return list.filter(Boolean);
    };

    if (params.search) chips.push({ key: 'search', label: `Ricerca: ${params.search}` });
    if (params.status) chips.push({ key: 'status', label: `Status contratto: ${getDossierStatusLabel(params.status)}` });
    if (params.contractType) chips.push({ key: 'contractType', label: `Tipo contratto: ${params.contractType}` });
    if (params.statoPreventivo) chips.push({ key: 'statoPreventivo', label: `Stato preventivo: ${params.statoPreventivo}` });

    asList(params.negotiationCategory).forEach((c) => {
      chips.push({ key: `negCat:${c}`, label: `Tipologia: ${c}` });
    });

    if (params.negotiationIssuer) {
      chips.push({ key: 'negIssuer', label: `Controparte: ${params.negotiationIssuer}` });
    }

    asList(params.negotiationStatus).forEach((s) => {
      chips.push({ key: `negStatus:${s}`, label: `Status pratica: ${s}` });
    });

    if (params.dataConferimentoFrom) {
      chips.push({
        key: 'dataConferimentoFrom',
        label: `Conferimento dal: ${formatDateLabel(params.dataConferimentoFrom)}`
      });
    }
    if (params.dataConferimentoTo) {
      chips.push({
        key: 'dataConferimentoTo',
        label: `Conferimento al: ${formatDateLabel(params.dataConferimentoTo)}`
      });
    }
    if (params.dataInvioDocumentiFrom) {
      chips.push({
        key: 'dataInvioDocumentiFrom',
        label: `Invio documenti dal: ${formatDateLabel(params.dataInvioDocumentiFrom)}`
      });
    }
    if (params.dataInvioDocumentiTo) {
      chips.push({
        key: 'dataInvioDocumentiTo',
        label: `Invio documenti al: ${formatDateLabel(params.dataInvioDocumentiTo)}`
      });
    }

    if (filterRecallStatus && filterRecallStatus !== 'all') {
      chips.push({
        key: 'recallStatus',
        label: `Stato recall: ${getRecallStatusLabel(filterRecallStatus)}`
      });
    }
    if (filterRecallOutcome) {
      chips.push({
        key: 'recallOutcome',
        label: `Esito recall: ${getRecallOutcomeLabel(filterRecallOutcome)}`
      });
    }

    if (params.respComm) chips.push({ key: 'respComm', label: `Resp. commerciale: ${params.respComm}` });
    if (params.respCont) chips.push({ key: 'respCont', label: `Resp. contabile: ${params.respCont}` });
    if (params.respUpsell) chips.push({ key: 'respUpsell', label: `Resp. upsell: ${params.respUpsell}` });

    return chips;
  };

  const handleOpenStandardExportDialog = () => {
    setStandardExportDialogOpen(true);
  };

  const handleConfirmStandardExport = async () => {
    setStandardExportDialogOpen(false);
    await handleStandardExport();
  };

  const fetchRowNegotiations = async (dossierId) => {
    setRowNegotiationsLoading((prev) => ({ ...prev, [dossierId]: true }));
    try {
      const res = await api.get(`/negotiation/dossier/${dossierId}`);
      setRowNegotiations((prev) => ({
        ...prev,
        [dossierId]: res.data || []
      }));
    } catch (error) {
      console.error('Error fetching negotiations for dossier:', error);
      toast.error('Errore nel caricamento delle pratiche collegate');
      setRowNegotiations((prev) => ({
        ...prev,
        [dossierId]: []
      }));
    } finally {
      setRowNegotiationsLoading((prev) => ({ ...prev, [dossierId]: false }));
    }
  };

  const handleToggleRowDetails = (dossierId) => {
    setExpandedRows((prev) => {
      const isExpanded = !!prev[dossierId];
      if (isExpanded) {
        return {};
      }
      return { [dossierId]: true };
    });
    if (!rowNegotiations[dossierId] && !rowNegotiationsLoading[dossierId]) {
      fetchRowNegotiations(dossierId);
    }
  };

  const getRecallStatusTooltip = (item) => {
    const status = item.recall_status || 'da_gestire';
    const outcome = item.recall_last_outcome || '';
    const when = item.recall_updated_at
      ? new Date(item.recall_updated_at).toLocaleString('it-IT')
      : null;
    const who = item.recall_updated_by || null;

    let base;
    if (status === 'gestito') base = 'Gestito';
    else if (status === 'non_gestire') base = 'Non gestire';
    else base = 'Da gestire';

    const parts = [base];
    if (outcome) parts.push(`esito: ${outcome.replace(/_/g, ' ')}`);
    if (who) parts.push(`da ${who}`);
    if (when) parts.push(`il ${when}`);
    return parts.join(' · ');
  };

  const openRecallDialog = (item) => {
    setRecallRow(item);
    setRecallOutcome('');
    setRecallNote('');
    setRecallDialogOpen(true);
  };

  const handleRecallConfirm = async () => {
    if (!recallRow || !recallOutcome) {
      toast.error('Seleziona un esito chiamata');
      return;
    }
    try {
      setRecallSaving(true);
      const res = await api.post(`/dossier/${recallRow.id}/recall-log`, {
        esito: recallOutcome,
        note: recallNote || ''
      });
      const log = res.data && res.data.log;
      const recallStatus = log && log.stato_recall ? log.stato_recall : 'da_gestire';
      const recallOutcomeValue =
        log && log.esito_chiamata ? log.esito_chiamata : recallOutcome;
      const recallUpdatedAt = log && log.created_at ? log.created_at : null;
      const recallUpdatedBy = log && log.user_name ? log.user_name : null;

      setList((prev) =>
        prev.map((row) =>
          row.id === recallRow.id
            ? {
                ...row,
                recall_status: recallStatus,
                recall_last_outcome: recallOutcomeValue,
                recall_updated_at: recallUpdatedAt,
                recall_updated_by: recallUpdatedBy
              }
            : row
        )
      );

      toast.success('Esito recall registrato');
      setRecallDialogOpen(false);
      setRecallRow(null);
    } catch (error) {
      console.error('Error saving recall log:', error);
      toast.error('Errore nel salvataggio dell\'esito recall');
    } finally {
      setRecallSaving(false);
    }
  };

  const openWorkflow = async (item) => {
    setWorkflowDossier(item);
    setWorkflowOpen(true);
    setWorkflowLoading(true);
    try {
      const res = await api.get(`/dossier/${item.id}/recall-log`);
      setWorkflowRows(res.data || []);
    } catch (error) {
      console.error('Error fetching recall workflow:', error);
      toast.error('Errore nel caricamento del workflow recall');
      setWorkflowRows([]);
    } finally {
      setWorkflowLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    filterSearch,
    filterStatus,
    filterContractType,
    filterRespComm,
    filterRespCont,
    filterRespUpsell,
    filterStatoPreventivo,
    filterNegotiationCategory,
    filterNegotiationIssuer,
    filterNegotiationStatus,
    filterDataConfFrom,
    filterDataConfTo,
    filterDataInvioDocFrom,
    filterDataInvioDocTo,
    filterRecallStatus,
    filterRecallOutcome,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/lorenzo-users');
        const names = res.data
          .map((u) => [u.name, u.surname].filter(Boolean).join(' '))
          .filter((n) => n);
        setUserNames([...new Set(names)].sort());
      } catch (error) {
        console.error('Error fetching users for dossier recall filters:', error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get('/dossier/filters/options');
        const data = res.data || {};
        setStatusOptions((data.statuses || []).filter(Boolean));
        setContractTypeOptions((data.contractTypes || []).filter(Boolean));
        setStatiPreventivoOptions((data.statiPreventivo || []).filter(Boolean));
        setNegotiationCategoryOptions((data.negotiationCategories || []).filter(Boolean));
        setNegotiationIssuerOptions((data.negotiationIssuers || []).filter(Boolean));
        setNegotiationStatusOptions((data.negotiationStatuses || []).filter(Boolean));
      } catch (error) {
        console.error('Error fetching dossier recall filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterBySearch = (event) => {
    setFilterSearch(event.target.value);
    setPage(0);
  };

  const handleFilterByStatus = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  const handleFilterByContractType = (event) => {
    setFilterContractType(event.target.value);
    setPage(0);
  };
  
  const handleFilterByStatoPreventivo = (event) => {
    setFilterStatoPreventivo(event.target.value);
    setPage(0);
  };

  const handleFilterByNegotiationCategory = (event, newValue) => {
    setFilterNegotiationCategory(Array.isArray(newValue) ? newValue : []);
    setPage(0);
  };

  const handleFilterByNegotiationStatus = (event, newValue) => {
    setFilterNegotiationStatus(Array.isArray(newValue) ? newValue : []);
    setPage(0);
  };

  const handleFilterByRecallStatus = (event) => {
    setFilterRecallStatus(event.target.value);
    setPage(0);
  };

  const handleOutcomeWidgetClick = (outcome) => {
    setFilterRecallOutcome((prev) => (prev === outcome ? '' : outcome));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activeFiltersCount = [
    filterSearch,
    filterStatus,
    filterContractType,
    filterRespComm,
    filterRespCont,
    filterRespUpsell,
    filterStatoPreventivo,
    Array.isArray(filterNegotiationCategory) && filterNegotiationCategory.length > 0 ? '1' : '',
    filterNegotiationIssuer,
    Array.isArray(filterNegotiationStatus) && filterNegotiationStatus.length > 0 ? '1' : '',
    filterRecallStatus && filterRecallStatus !== 'all' ? '1' : '',
    filterRecallOutcome,
    filterDataConfFrom || filterDataConfTo ? '1' : '',
    filterDataInvioDocFrom || filterDataInvioDocTo ? '1' : ''
  ].filter(Boolean).length;

  const sortedList = [...list].sort((a, b) => {
    const isAsc = order === 'asc';
    if (orderBy === 'id' || orderBy === 'importo_totale' || orderBy === 'acconto') {
      return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
    }
    const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
    const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
    if (valA < valB) return isAsc ? -1 : 1;
    if (valA > valB) return isAsc ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Headphones size={20} color="#0284c7" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Recall Preventivi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selezione dei preventivi da richiamare sui clienti
            </Typography>
          </Box>
        </Box>
        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon size={18} />}
              sx={{ textTransform: 'none' }}
              onClick={handleOpenStandardExportDialog}
              disabled={standardExporting}
            >
              {standardExporting ? 'Avvio in corso...' : 'Esporta'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Phone size={18} />}
              sx={{ textTransform: 'none' }}
              onClick={() => setCloudtalkDialogOpen(true)}
              disabled={cloudtalkExporting}
            >
              {cloudtalkExporting ? 'Avvio in corso...' : 'CSV per Cloudtalk (Dialer)'}
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ flex: '1 1 0', minWidth: 180 }}>
          <RecallStatWidget
            title="Non risposto"
            value={esitoStats.non_risposto}
            icon={HelpCircle}
            color="#0ea5e9"
            active={filterRecallOutcome === 'non_risposto'}
            onClick={() => handleOutcomeWidgetClick('non_risposto')}
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 180 }}>
          <RecallStatWidget
            title="Non buono"
            value={esitoStats.non_buono}
            icon={XCircle}
            color="#f59e0b"
            active={filterRecallOutcome === 'non_buono'}
            onClick={() => handleOutcomeWidgetClick('non_buono')}
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 180 }}>
          <RecallStatWidget
            title="Preventivo scritto"
            value={esitoStats.preventivo_scritto}
            icon={CheckCircle2}
            color="#6366f1"
            active={filterRecallOutcome === 'preventivo_scritto'}
            onClick={() => handleOutcomeWidgetClick('preventivo_scritto')}
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 180 }}>
          <RecallStatWidget
            title="Richiamare"
            value={esitoStats.richiamare}
            icon={Phone}
            color="#0ea5e9"
            active={filterRecallOutcome === 'richiamare'}
            onClick={() => handleOutcomeWidgetClick('richiamare')}
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 180 }}>
          <RecallStatWidget
            title="Cliente"
            value={esitoStats.cliente}
            icon={CheckCircle2}
            color="#22c55e"
            active={filterRecallOutcome === 'cliente'}
            onClick={() => handleOutcomeWidgetClick('cliente')}
          />
        </Box>
      </Box>

      <StyledCard>
        <Grid container spacing={2} sx={{ mb: 3, p: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Cerca per reference, descrizione, ID..."
              value={filterSearch}
              onChange={handleFilterBySearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {filterSearch && (
                        <IconButton size="small" onClick={() => setFilterSearch('')}>
                          <X size={20} />
                        </IconButton>
                      )}
                      <IconButton onClick={() => setShowFilters(!showFilters)}>
                        <Badge badgeContent={activeFiltersCount} color="primary">
                          <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                        </Badge>
                      </IconButton>
                      <Tooltip title="Seleziona colonne aggiuntive">
                        <IconButton onClick={handleOpenColumnMenu}>
                          <ViewColumnIcon color={optionalColumnsActive ? 'primary' : 'action'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          {showFilters && (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1 }}>
                  <FormControl size="small" sx={{ width: 260, flex: '0 0 auto' }}>
                    <InputLabel>Status Contratto</InputLabel>
                    <Select value={filterStatus} label="Status Contratto" onChange={handleFilterByStatus}>
                      <MenuItem value=""><em>Tutti</em></MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {getDossierStatusLabel(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ width: 260, flex: '0 0 auto' }}>
                    <InputLabel>Tipo Contratto</InputLabel>
                    <Select
                      value={filterContractType}
                      label="Tipo Contratto"
                      onChange={handleFilterByContractType}
                    >
                      <MenuItem value=""><em>Tutti</em></MenuItem>
                      {contractTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ width: 260, flex: '0 0 auto' }}>
                    <InputLabel>Stato Preventivo</InputLabel>
                    <Select
                      value={filterStatoPreventivo}
                      label="Stato Preventivo"
                      onChange={handleFilterByStatoPreventivo}
                    >
                      <MenuItem value=""><em>Tutti</em></MenuItem>
                      {statiPreventivoOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    options={Array.from(new Set(['Non assegnata', ...negotiationCategoryOptions]))}
                    value={filterNegotiationCategory}
                    onChange={handleFilterByNegotiationCategory}
                    filterSelectedOptions
                    disableCloseOnSelect
                    limitTags={1}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipologia pratica"
                        placeholder="Tutte"
                        size="small"
                      />
                    )}
                    sx={{ width: 260, flex: '0 0 auto' }}
                    clearOnEscape
                  />

                  <Autocomplete
                    options={negotiationIssuerOptions}
                    value={filterNegotiationIssuer || null}
                    onChange={(event, newValue) => {
                      setFilterNegotiationIssuer(newValue || '');
                      setPage(0);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Controparte"
                        placeholder="Tutte"
                        size="small"
                      />
                    )}
                    sx={{ width: 260, flex: '0 0 auto' }}
                    clearOnEscape
                  />

                  <Autocomplete
                    multiple
                    options={negotiationStatusOptions}
                    value={filterNegotiationStatus}
                    onChange={handleFilterByNegotiationStatus}
                    filterSelectedOptions
                    disableCloseOnSelect
                    limitTags={1}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status pratiche"
                        placeholder="Tutti"
                        size="small"
                      />
                    )}
                    sx={{ width: 260, flex: '0 0 auto' }}
                    clearOnEscape
                  />

                  <FormControl size="small" sx={{ width: 260, flex: '0 0 auto' }}>
                    <InputLabel>Stato recall</InputLabel>
                    <Select
                      value={filterRecallStatus}
                      label="Stato recall"
                      onChange={handleFilterByRecallStatus}
                    >
                      <MenuItem value="all"><em>Tutti</em></MenuItem>
                      <MenuItem value="da_gestire">Da gestire</MenuItem>
                      <MenuItem value="gestito">Gestito</MenuItem>
                      <MenuItem value="non_gestire">Non gestire</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', overflowX: 'auto', pt: 1 }}>
                  <TextField
                    label="Data conferimento da"
                    type="date"
                    size="small"
                    value={filterDataConfFrom}
                    onChange={(e) => {
                      setFilterDataConfFrom(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 220, flex: '0 0 auto' }}
                  />
                  <TextField
                    label="Data conferimento a"
                    type="date"
                    size="small"
                    value={filterDataConfTo}
                    onChange={(e) => {
                      setFilterDataConfTo(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 220, flex: '0 0 auto' }}
                  />
                  <TextField
                    label="Data invio documenti da"
                    type="date"
                    size="small"
                    value={filterDataInvioDocFrom}
                    onChange={(e) => {
                      setFilterDataInvioDocFrom(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 220, flex: '0 0 auto' }}
                  />
                  <TextField
                    label="Data invio documenti a"
                    type="date"
                    size="small"
                    value={filterDataInvioDocTo}
                    onChange={(e) => {
                      setFilterDataInvioDocTo(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 220, flex: '0 0 auto' }}
                  />
                  <Tooltip title="Reset date">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleClearDateFilters}
                        disabled={!hasDateFilters}
                        sx={{ flex: '0 0 auto', mt: 1 }}
                      >
                        <X size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={userNames}
                  value={filterRespComm || null}
                  onChange={(event, newValue) => {
                    setFilterRespComm(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Commerciale" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={userNames}
                  value={filterRespCont || null}
                  onChange={(event, newValue) => {
                    setFilterRespCont(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Contabile" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={userNames}
                  value={filterRespUpsell || null}
                  onChange={(event, newValue) => {
                    setFilterRespUpsell(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Upsell" />}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>

        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Colonne aggiuntive
            </Typography>
          </Box>
          <FormGroup sx={{ px: 2, pb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.id}
                  onChange={() => handleToggleColumn('id')}
                />
              }
              label="ID"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.responsabile}
                  onChange={() => handleToggleColumn('responsabile')}
                />
              }
              label="Resp. Comm."
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.acconto}
                  onChange={() => handleToggleColumn('acconto')}
                />
              }
              label="Acconto"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.dataInvioDocumenti}
                  onChange={() => handleToggleColumn('dataInvioDocumenti')}
                />
              }
              label="Data Invio Documenti"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.dataConferimento}
                  onChange={() => handleToggleColumn('dataConferimento')}
                />
              }
              label="Data Conferimento"
            />
          </FormGroup>
        </Menu>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell />
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Da gestire
                  </Typography>
                </TableCell>
                {columnVisibility.id && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'reference'}
                    direction={orderBy === 'reference' ? order : 'asc'}
                    onClick={() => handleRequestSort('reference')}
                  >
                    Reference
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'rif_customer'}
                    direction={orderBy === 'rif_customer' ? order : 'asc'}
                    onClick={() => handleRequestSort('rif_customer')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.responsabile && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'responsabile_commerciale'}
                      direction={orderBy === 'responsabile_commerciale' ? order : 'asc'}
                      onClick={() => handleRequestSort('responsabile_commerciale')}
                    >
                      Resp. Comm.
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'importo_totale'}
                    direction={orderBy === 'importo_totale' ? order : 'asc'}
                    onClick={() => handleRequestSort('importo_totale')}
                  >
                    Importo Totale
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.acconto && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'acconto'}
                      direction={orderBy === 'acconto' ? order : 'asc'}
                      onClick={() => handleRequestSort('acconto')}
                    >
                      Acconto
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataInvioDocumenti && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_invio_documenti'}
                      direction={orderBy === 'data_invio_documenti' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_invio_documenti')}
                    >
                      Data Invio Documenti
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataConferimento && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_conferimento'}
                      direction={orderBy === 'data_conferimento' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_conferimento')}
                    >
                      Data Conferimento
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : sortedList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun preventivo trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedList.map((item) => (
                    <React.Fragment key={item.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleRowDetails(item.id)}
                            aria-label="Dettagli pratiche collegate"
                          >
                            {expandedRows[item.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 32
                            }}
                          >
                            {item.recall_status === 'gestito' ? (
                              <Tooltip title={getRecallStatusTooltip(item)} arrow>
                                <CheckCircle2 size={20} color="#22c55e" />
                              </Tooltip>
                            ) : item.recall_status === 'non_gestire' ? (
                              <Tooltip title={getRecallStatusTooltip(item)} arrow>
                                <XCircle size={20} color="#dc2626" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Gestisci recall" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => openRecallDialog(item)}
                                >
                                  <HelpCircle size={20} color="#dc2626" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {item.recall_last_outcome && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.25 }}
                              >
                                {getRecallOutcomeLabel(item.recall_last_outcome)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {columnVisibility.id && <TableCell>{item.id}</TableCell>}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {item.reference}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography sx={{ fontWeight: 500 }}>
                              {item.customer_ragione_sociale ||
                                ((item.customer_name || item.customer_surname)
                                  ? `${item.customer_name || ''} ${item.customer_surname || ''}`.trim()
                                  : item.rif_customer
                                  ? `ID: ${item.rif_customer}`
                                  : '-')}
                            </Typography>
                            {item.customer_email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Mail size={14} />
                                <Typography variant="caption" color="text.secondary">
                                  {item.customer_email}
                                </Typography>
                              </Box>
                            )}
                            {item.customer_telephone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Phone size={14} />
                                <Typography variant="caption" color="text.secondary">
                                  {item.customer_telephone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        {columnVisibility.responsabile && (
                          <TableCell>{item.responsabile_commerciale || '-'}</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                          {Number(item.importo_totale || 0).toLocaleString('it-IT', {
                            style: 'currency',
                            currency: 'EUR',
                          })}
                        </TableCell>
                        {columnVisibility.acconto && (
                          <TableCell>
                            {Number(item.acconto || 0).toLocaleString('it-IT', {
                              style: 'currency',
                              currency: 'EUR',
                            })}
                          </TableCell>
                        )}
                        {columnVisibility.dataInvioDocumenti && (
                          <TableCell>
                            {item.data_invio_documenti
                              ? new Date(item.data_invio_documenti).toLocaleDateString('it-IT')
                              : '-'}
                          </TableCell>
                        )}
                        {columnVisibility.dataConferimento && (
                          <TableCell>
                            {item.data_conferimento
                              ? new Date(item.data_conferimento).toLocaleDateString('it-IT')
                              : '-'}
                          </TableCell>
                        )}
                        <TableCell>
                          {item.status ? (
                            <Chip label={getDossierStatusLabel(item.status)} size="small" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Workflow recall contatto">
                            <IconButton size="small" onClick={() => openWorkflow(item)}>
                              <Activity size={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {expandedRows[item.id] && (
                        <TableRow>
                          <TableCell colSpan={totalColumns}>
                            {rowNegotiationsLoading[item.id] ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : (rowNegotiations[item.id] || []).length === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                Nessuna pratica collegata trovata per questo preventivo.
                              </Typography>
                            ) : (
                              <Box sx={{ py: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  Pratiche collegate
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Riferimento</TableCell>
                                      <TableCell>Controparte</TableCell>
                                      <TableCell>Tipologia</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(rowNegotiations[item.id] || []).map((neg) => (
                                      <TableRow key={neg.id} hover>
                                        <TableCell>{neg.reference}</TableCell>
                                        <TableCell>{neg.issuer_name || '-'}</TableCell>
                                        <TableCell>{neg.category_name || '-'}</TableCell>
                                        <TableCell>
                                          <Chip
                                            label={neg.status}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={standardExportDialogOpen}
        onClose={() => (standardExporting ? null : setStandardExportDialogOpen(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conferma esportazione CSV Recall</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Verrà avviato un export asincrono in base ai filtri selezionati. Il file sarà disponibile
              nelle notifiche.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {getStandardExportFilterChips().length === 0 ? (
                <Chip label="Nessun filtro" size="small" variant="outlined" />
              ) : (
                getStandardExportFilterChips().map((c) => (
                  <Chip key={c.key} label={c.label} size="small" variant="outlined" />
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStandardExportDialogOpen(false)} disabled={standardExporting}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmStandardExport}
            variant="contained"
            disabled={standardExporting}
            startIcon={<DownloadIcon size={18} />}
          >
            {standardExporting ? 'Avvio in corso...' : 'Avvia export'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloudtalkDialogOpen} onClose={() => setCloudtalkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>CSV per Cloudtalk (Dialer)</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Inserisci il <strong>tag</strong> che verrà associato a tutti i nominativi esportati
              (es. "recall_marzo", "promo_prestito").
            </Typography>
            <TextField
              fullWidth
              label="Tag Cloudtalk"
              value={cloudtalkTag}
              onChange={(e) => setCloudtalkTag(e.target.value)}
              placeholder="es. recall_marzo_2026"
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={cloudtalkStripPhonePrefix}
                  onChange={(e) => setCloudtalkStripPhonePrefix(e.target.checked)}
                />
              }
              label='Esporta il telefono senza prefisso "+39" (se presente)'
            />
            <Typography
              variant="caption"
              sx={{
                color: cloudtalkStripPhonePrefix ? 'success.main' : 'text.secondary',
                fontWeight: cloudtalkStripPhonePrefix ? 600 : 400
              }}
            >
              {cloudtalkStripPhonePrefix
                ? 'Esempio telefono: +393491234567 -> 3491234567'
                : 'Esempio telefono: +393491234567 -> +393491234567'}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="cloudtalk-separator-label">Separatore CSV</InputLabel>
              <Select
                labelId="cloudtalk-separator-label"
                label="Separatore CSV"
                value={cloudtalkSeparator}
                onChange={(e) => setCloudtalkSeparator(e.target.value)}
              >
                <MenuItem value=",">Virgola (,)</MenuItem>
                <MenuItem value=";">Punto e virgola (;)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloudtalkDialogOpen(false)}>Annulla</Button>
          <Button
            onClick={handleCloudtalkExport}
            variant="contained"
            color="primary"
            disabled={cloudtalkExporting}
          >
            {cloudtalkExporting ? 'Avvio in corso...' : 'Conferma esportazione'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recallDialogOpen}
        onClose={() => (recallSaving ? null : setRecallDialogOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Esito chiamata recall</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Seleziona l&apos;esito della chiamata per il preventivo{' '}
              <strong>{recallRow?.reference || ''}</strong>.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Esito chiamata</InputLabel>
              <Select
                label="Esito chiamata"
                value={recallOutcome}
                onChange={(e) => setRecallOutcome(e.target.value)}
              >
                <MenuItem value="non_risposto">Non risposto</MenuItem>
                <MenuItem value="non_buono">Non buono</MenuItem>
                <MenuItem value="preventivo_scritto">Preventivo scritto</MenuItem>
                <MenuItem value="richiamare">Richiamare</MenuItem>
                <MenuItem value="cliente">Cliente</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Note (opzionale)"
              value={recallNote}
              onChange={(e) => setRecallNote(e.target.value)}
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecallDialogOpen(false)} disabled={recallSaving}>
            Annulla
          </Button>
          <Button
            onClick={handleRecallConfirm}
            variant="contained"
            disabled={recallSaving}
          >
            {recallSaving ? 'Salvataggio...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={workflowOpen}
        onClose={() => setWorkflowOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            p: 2,
            boxSizing: 'border-box'
          }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Workflow Recall</Typography>
          {workflowDossier && (
            <Typography variant="body2" color="text.secondary">
              {workflowDossier.reference} ·{' '}
              {workflowDossier.customer_ragione_sociale ||
                ((workflowDossier.customer_name || workflowDossier.customer_surname)
                  ? `${workflowDossier.customer_name || ''} ${workflowDossier.customer_surname || ''}`.trim()
                  : workflowDossier.rif_customer)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {workflowLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !workflowRows.length ? (
          <Typography variant="body2" color="text.secondary">
            Nessun richiamo registrato per questo preventivo.
          </Typography>
        ) : (
          <Box sx={{ position: 'relative', pl: 2 }}>
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                top: 4,
                bottom: 4,
                width: 2,
                bgcolor: 'divider'
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {workflowRows.map((row, index) => {
                const { Icon, color, bg } = getWorkflowIconConfig(
                  row.esito_chiamata,
                  row.stato_recall
                );
                return (
                  <Box key={row.id} sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      sx={{
                        mt: 0.5,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: bg,
                        color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        border: '2px solid white',
                        boxShadow: '0 0 0 1px rgba(148, 163, 184, 0.5)'
                      }}
                    >
                      <Icon size={12} />
                    </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">
                      {getRecallOutcomeLabel(row.esito_chiamata)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.stato_recall === 'gestito'
                        ? 'Gestito'
                        : row.stato_recall === 'non_gestire'
                        ? 'Non gestire'
                        : 'Da gestire'}
                      {row.user_name ? ` · ${row.user_name}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString('it-IT')
                        : ''}
                    </Typography>
                    {row.note && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                      >
                        {row.note}
                      </Typography>
                    )}
                  </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
